import { buttonClasses } from '@mui/material/Button'
import { stackClasses } from '@mui/material/Stack'
import { Theme } from '@mui/material/styles'
import { tableCellClasses } from '@mui/material/TableCell'
import { tableSortLabelClasses } from '@mui/material/TableSortLabel'

import { capCentred } from 'src/design-system/textBaseline'

import { stripTextHover } from '../hover'
import {
  colorTokens,
  icons,
  layout,
  typography as typeTokens,
} from '../tokens.generated'
import { tabularNumerals } from '../typography'
import { GHOST_EDGE } from './Button'

// ----------------------------------------------------------------------
// The table geometry, decided once. Head cells are 42 and sticky, body cells
// are 48 with a 10 / 16 inset, and the first and last column carry a 20px
// gutter so the content lines up with a card's padding around it.
//
// Borders are `separate`. A collapsed border belongs to the table, so it stays
// behind when a sticky header scrolls away; a separate border belongs to the
// cell and travels with it, which is what keeps the line under the header
// where the header is.
//
// A pressable row takes the one surface answer. A selected row answers with
// its ink instead: its fill is already saying it is the selected one, and a
// second fill on top of that read as nothing happening at all.
// ----------------------------------------------------------------------

/**
 * The line that frames a data panel: its outer edge, and the rules that close
 * a head band and a foot.
 *
 * **The fill cannot carry this and the boundary has to.** Measured across the
 * three surface roles, in both schemes: a panel is `surface`, the page under
 * it is `surface2` and the bands are `surface2` as well, because the token
 * source names `surface2` for the app background *and* for the opaque fill
 * behind a sticky table header, so a band is not merely near the page, it is
 * the page, at 1.000:1. Paper against that page is 1.035:1 in light and
 * 1.083:1 in dark, and the widest move available inside the surface set,
 * `surfaceSunken`, buys 1.091 and 1.110. Nothing there clears a floor.
 *
 * So the edge is the only thing saying where a panel ends, and it used to be
 * `divider`, the same hairline the rows *inside* it are separated by, at
 * 1.105:1 against the page in light and 1.321:1 in dark. The panel's outline
 * was one of its own row rules. `borderStrong` takes it to 1.384 and 1.645
 * against the page, and gives the frame a rank over what it encloses: 1.432
 * against the paper the rows sit on, where the row rule is 1.144.
 *
 * Not `controlEdge`, which is 3.72:1 on paper. That role exists because a
 * 20px checkbox has nothing but its boundary to say it is there. A 1px
 * mid-grey line around a full-width panel is a box, and a card is flat.
 */
export const panelEdge = (theme: Theme) =>
  `1px solid ${colorTokens[theme.palette.mode].borderStrong}`

/**
 * How much of a sortable column's arrow shows while the column is not the
 * sorted one: at rest, and under the pointer or the keyboard. MUI's are 0 and
 * 0.5. The sorted column's arrow is whole.
 */
export const SORT_ARROW_AT_REST = 0.35
export const SORT_ARROW_POINTED = 0.7

/**
 * What a cell may hold for its words to be trimmed to their capitals: none of
 * these. A chip, a button, an initial, a code tag, a zone tag, a glyph or a
 * field sets its own words on its own box, and a cell that trimmed its first
 * line to its text would move that box instead: the Download button dropped
 * 3px when every cell was trimmed.
 */
const SETS_ITS_OWN_WORDS = [
  '.MuiButtonBase-root',
  '.MuiChip-root',
  '.MuiAvatar-root',
  '[data-testid="code-tag"]',
  '[data-zone-note]',
  'svg',
  'img',
  'input',
  'textarea',
  '[role="progressbar"]',
].join(', ')

/** Each button size's class, and the inset its label stands at. */
const GHOST_INSETS = [
  [buttonClasses.sizeSmall, layout.controlInsetSmall],
  [buttonClasses.sizeMedium, layout.controlInset],
  [buttonClasses.sizeLarge, layout.controlInsetLarge],
] as const

/**
 * **A ghost on a column's edge puts its word on that edge, not its box.**
 *
 * A text button draws no edge at rest, so what reads as its position is its
 * word, and the word stands inside the box by the reserved edge and the
 * control's inset, 13px at small, and further still where MUI's 64px minimum
 * centres a short word. Measured on the preview: `Waive` sat 14.5px right of
 * the `Actions` it was listed under, and every right-aligned `Dismiss`,
 * `Remove` and `Withdraw` stopped 13px short of its column's edge, where the
 * head's label and every figure above and below it end.
 *
 * So the ghost on the edge gives up the minimum and is pulled out by its own
 * inset. Its hover fill reaches into the cell's padding, which is there to
 * hold it. The edge is the start of a left-aligned cell and the end of a
 * right-aligned one; a centred cell has none. The ghost on it is the cell's
 * first or last button, or the first or last of a row of buttons (`Stack`)
 * that is the cell's content. An outlined or filled button is left alone: its box
 * is drawn, and the box is what belongs on the edge.
 */
const ghostOnTheEdge = () => {
  const start = `&:not(.${tableCellClasses.alignRight}):not(.${tableCellClasses.alignCenter})`
  const end = `&.${tableCellClasses.alignRight}`
  const onEdge = (cell: string, edge: 'first' | 'last', size: string) => {
    // Of type, not child: Emotion refuses `:first-child` as unsafe where a
    // page is rendered on a server, and a cell's buttons are its only buttons.
    const ghost = `.${buttonClasses.text}.${size}:${edge}-of-type`
    return `${cell} > ${ghost}, ${cell} > .${stackClasses.root}:${edge}-of-type > ${ghost}`
  }

  return Object.fromEntries(
    GHOST_INSETS.flatMap(([size, inset]) => [
      [
        onEdge(start, 'first', size),
        { minWidth: 0, marginLeft: -(inset + GHOST_EDGE) },
      ],
      [
        onEdge(end, 'last', size),
        { minWidth: 0, marginRight: -(inset + GHOST_EDGE) },
      ],
    ]),
  )
}

/**
 * **The same, for a value standing on a phone card's edge.** Below `md` a
 * row's value sits on the card's trailing edge, beside its label, and a ghost
 * there (the rentals lists' copyable rental number) stood its inset short of
 * every value above and below it. A right-aligned value pulls its last ghost
 * out by the ghost's own inset, as a right-aligned cell does; a left-aligned
 * one its first. Applied by `ResponsiveTable` to the card value it draws.
 *
 * The lengths are written in pixels because they land in an `sx`, where a
 * bare number is a spacing step: `-13` there is 104px.
 */
export const ghostOnTheValueEdge = (edge: 'start' | 'end') =>
  Object.fromEntries(
    GHOST_INSETS.map(([size, inset]) =>
      edge === 'end'
        ? [
            `& > .${buttonClasses.text}.${size}:last-of-type`,
            { minWidth: 0, marginRight: `-${inset + GHOST_EDGE}px` },
          ]
        : [
            `& > .${buttonClasses.text}.${size}:first-of-type`,
            { minWidth: 0, marginLeft: `-${inset + GHOST_EDGE}px` },
          ],
    ),
  )

export default function Table(theme: Theme) {
  const gutter = theme.spacing(2.5)

  return {
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate' as const,
          borderSpacing: 0,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          'transition': theme.motion.state.transition('background-color'),
          '&.MuiTableRow-hover:hover': {
            backgroundColor: theme.hover.surface,
          },
          '&.Mui-selected': {
            'backgroundColor': theme.palette.primary.lighter,
            '&:hover': {
              backgroundColor: theme.palette.primary.lighter,
              color: theme.hover.selectedInk,
            },
          },
          // Flat design tables drop the divider on the final row for a clean edge.
          '&:last-of-type td.MuiTableCell-body': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        // The single, canonical table header: a flat near-surface strip with
        // small secondary-coloured labels. No heavy grey fill and no vertical
        // cell borders. It sticks to the top of whatever scrolls it, above the
        // rows that pass under it, and it is opaque so they do not show
        // through. Height plus centring sets the row; there is no block
        // padding to fight it.
        head: {
          'position': 'sticky' as const,
          'top': 0,
          'zIndex': 2,
          'color': theme.palette.text.secondary,
          'backgroundColor': theme.palette.background.default,
          'borderBottom': `1px solid ${theme.palette.divider}`,
          'fontSize': typeTokens.console.caption.size,
          'fontWeight': typeTokens.weights.semibold,
          'height': layout.tableHeaderHeight,
          'paddingInline': theme.spacing(2),
          'paddingBlock': 0,
          'whiteSpace': 'nowrap',
          'letterSpacing': '-0.002em',
          '&:first-of-type': {
            paddingLeft: gutter,
          },
          '&:last-of-type': {
            paddingRight: gutter,
          },
        },
        stickyHeader: {
          backgroundColor: theme.palette.background.default,
        },
        body: {
          'height': layout.tableRowHeight,
          'padding': theme.spacing(1.25, 2),
          // Tabular numerals for every cell. A battery column that ticks, a
          // countdown and a money column all need digits of one width, and a
          // table is the one place the system can apply that centrally instead
          // of asking 37 screens to remember.
          ...tabularNumerals,
          '&:first-of-type': {
            paddingLeft: gutter,
          },
          '&:last-of-type': {
            paddingRight: gutter,
          },
          // **A cell's words sit on the row's middle by their capitals.**
          // Centred by its line, which Heebo makes tall at the top, a cell of
          // text read 1.5px high of the Download beside it and 0.8px high of
          // a status chip (asked for on 2026-11-02: `Sent once it is paid`
          // sat higher than `Open` and `Download`); on its capitals it is
          // within half a pixel of both. Only a cell of text: see
          // `SETS_ITS_OWN_WORDS`. A chip's own word is not trimmed, because a
          // chip clips its label's box and a trimmed box cut off the `g` of
          // `Pending`. A browser without `text-box` (Firefox, today) draws it
          // as before.
          [`&:not(:has(${SETS_ITS_OWN_WORDS}))`]: capCentred,
          ...ghostOnTheEdge(),
        },
      },
    },
    // A column header is a text control, and MUI's own hover on it resolves to
    // the ink the header cell above already carries: the rule is there, it
    // computes to the colour that was already painted, and the whole header
    // row ignores the pointer. The underline is the part that cannot be a
    // no-op, whether the column is the sorted one or not. The keyboard gets
    // the same, and no ring: a ring here was a box round one word in a row of
    // words (`stripTextHover`).
    //
    // **A sortable column says so before it is pointed at.** MUI hides the
    // arrow of every column but the sorted one until the pointer is over it,
    // so a row of heads read as plain labels and nobody found the sort
    // (asked on 2026-11-01 for the arrows to show, a little transparent, at
    // rest). The sorted column's arrow is whole; the others' stand at rest at
    // a step that reads as there but not chosen, and rise under the pointer
    // or the keyboard.
    //
    // **The whole head cell is the control, and a pointed arrow stays below
    // the sorted one** (the head row "glitched on hover", 2026-11-02). Only
    // the word and its arrow answered, a third of the cell, so a pointer
    // moving along the row lit and dropped each head as it crossed the
    // padding between them. The control now reaches over its cell through
    // a layer of its own, positioned on the cell, which is sticky and so
    // holds it. And the arrow took the label's ink under the pointer, so a
    // pointed column's arrow was as dark as the sorted column's and for a
    // moment two columns read as the sorted one: it keeps the quiet ink in
    // every state, and only its opacity says which column is sorted. The
    // arrow moves on the label's own duration, so the two change together.
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          ...stripTextHover(theme, theme.palette.text.primary),
          // Static, so the layer below is placed on the cell and not on the
          // label. ButtonBase is relative only to hold a ripple, and a sort
          // label draws none.
          'position': 'static',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
          },
          [`&:hover .${tableSortLabelClasses.icon}`]: {
            opacity: SORT_ARROW_POINTED,
          },
          [`&:focus-visible .${tableSortLabelClasses.icon}, &.Mui-focusVisible .${tableSortLabelClasses.icon}`]:
            { opacity: SORT_ARROW_POINTED },
          [`&.${tableSortLabelClasses.active} .${tableSortLabelClasses.icon}`]:
            { opacity: 1 },
        },
        icon: {
          opacity: SORT_ARROW_AT_REST,
          color: theme.palette.text.secondary,
          transition: theme.motion.state.transition('opacity', 'transform'),
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          // The pager is a foot band, so it closes on the frame rule and not
          // on the hairline that separates one row from the next.
          borderTop: panelEdge(theme),
        },
        toolbar: {
          height: layout.footerHeight,
        },
        select: {
          '&:focus': {
            borderRadius: theme.radii.control,
          },
        },
        // The rows-per-page chevron is a nav-step glyph. MUI centres its own
        // 24px icon with an em offset, so a 20px glyph is centred explicitly
        // from its own size rather than nudged by a magic margin.
        selectIcon: {
          width: icons.sizes.toolbar,
          height: icons.sizes.toolbar,
          top: `calc(50% - ${icons.sizes.toolbar / 2}px)`,
        },
      },
    },
  }
}
